import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import type { SubscriptionTier } from '../../store/slices/userSlice';
import strings from '../../utils/strings';
import routes from '../../utils/routes';
import { CText, CButton, CLoader, SEO } from '../../components/common';
import { FiPlus } from 'react-icons/fi';
import { getAuthToken } from '../../utils/localStorage';
import {
  fetchWalletInfo,
  fetchRazorpayKeyId,
  createWalletDepositOrder,
  verifyWalletDeposit,
  toggleAutoRenew,
  WalletTransaction,
} from '../../api/expert';

declare global {
  interface Window {
    Razorpay: any;
  }
}

const PRESET_AMOUNTS = [100, 500, 1000, 5000];

type TransactionFilter = 'ALL' | 'DEPOSIT' | 'SUBSCRIPTION' | 'BOOKING';

const TIER_CONFIG: Record<SubscriptionTier, { label: string; color: string; bg: string }> = {
  free: { label: 'Free', color: '#64748B', bg: '#F1F5F9' },
  pro: { label: 'Pro', color: '#6B39F4', bg: '#F8F5FF' },
  premium: { label: 'Premium', color: '#D97706', bg: '#FFFBEB' },
};

const PLAN_BENEFITS: Record<SubscriptionTier, { queries: string; buckets: string; discount: string }> = {
  free: { queries: '5 per week', buckets: '2 free buckets', discount: 'No discount' },
  pro: { queries: 'Unlimited', buckets: 'All 15+ buckets', discount: '10% off expert sessions' },
  premium: { queries: 'Unlimited', buckets: 'All 15+ buckets', discount: '25% off + 1 free session' },
};

const Wallet: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [balance, setBalance] = useState('0.00');
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [error, setError] = useState('');
  const [txFilter, setTxFilter] = useState<TransactionFilter>('ALL');
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('free');
  const [subscriptionExpires, setSubscriptionExpires] = useState<string | null>(null);
  const [subscriptionTenure, setSubscriptionTenure] = useState<string>('monthly');
  const [subscriptionPricePaid, setSubscriptionPricePaid] = useState<string>('0');
  const [autoRenew, setAutoRenew] = useState<boolean>(true);
  const [autoRenewLoading, setAutoRenewLoading] = useState(false);
  const [copilotQueriesUsed, setCopilotQueriesUsed] = useState(0);
  const [queriesResetAt, setQueriesResetAt] = useState<string | null>(null);

  const loadWalletInfo = useCallback(async () => {
    if (!getAuthToken()) {
      navigate(routes.login);
      return;
    }
    try {
      const info = await fetchWalletInfo();
      setBalance(info.wallet_balance);
      setTransactions(info.transactions);
      setSubscriptionTier((info.subscription_tier as SubscriptionTier) || 'free');
      setSubscriptionExpires(info.subscription_expires);
      setSubscriptionTenure(info.subscription_tenure || 'monthly');
      setSubscriptionPricePaid(info.subscription_price_paid || '0');
      setAutoRenew(info.auto_renew ?? true);
      setCopilotQueriesUsed(info.copilot_queries_used ?? 0);
      setQueriesResetAt(info.copilot_queries_reset_at);
    } catch (err) {
      console.error('Failed to load wallet info:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadWalletInfo();
  }, [loadWalletInfo]);

  const handleDeposit = async () => {
    setError('');
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setDepositing(true);
    try {
      const keyId = await fetchRazorpayKeyId();
      const order = await createWalletDepositOrder(amount);

      const options = {
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Yantrai',
        description: `Wallet Deposit - Rs ${amount}`,
        order_id: order.order_id,
        handler: async (response: any) => {
          try {
            const result = await verifyWalletDeposit({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setBalance(result.wallet_balance);
            setDepositAmount('');
            await loadWalletInfo();
          } catch (err) {
            setError('Payment verification failed. Contact support if money was deducted.');
          }
        },
        modal: {
          ondismiss: () => {
            setDepositing(false);
          },
        },
        theme: {
          color: colors.primary,
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setError('Payment failed. Please try again.');
        setDepositing(false);
      });
      rzp.open();
    } catch (err) {
      setError('Could not initiate payment. Please try again.');
      setDepositing(false);
    }
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatExpiryDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredTransactions = transactions.filter((t) => {
    if (txFilter === 'ALL') return true;
    if (txFilter === 'DEPOSIT') return t.transaction_type === 'DEPOSIT';
    if (txFilter === 'SUBSCRIPTION') return t.description?.toLowerCase().includes('subscription');
    if (txFilter === 'BOOKING') return t.description?.toLowerCase().includes('booking');
    return true;
  });

  const getStatusColor = (txStatus: string) => {
    switch (txStatus) {
      case 'SUCCESS': return colors.green || '#1DCE5C';
      case 'PENDING': return '#F59E0B';
      case 'FAILED': return colors.alertColor || '#E82C81';
      default: return colors.grayScale500;
    }
  };

  const tierConfig = TIER_CONFIG[subscriptionTier];
  const benefits = PLAN_BENEFITS[subscriptionTier];
  const isPaid = subscriptionTier !== 'free';
  const queriesLimit = 5;

  const handleToggleAutoRenew = async () => {
    setAutoRenewLoading(true);
    try {
      const result = await toggleAutoRenew(!autoRenew);
      setAutoRenew(result.auto_renew);
    } catch {
      // silent fail
    } finally {
      setAutoRenewLoading(false);
    }
  };

  const tenureLabel = subscriptionTenure === 'half_yearly'
    ? 'Half-Yearly'
    : subscriptionTenure === 'yearly'
    ? 'Yearly'
    : 'Monthly';

  const renewalPrice = parseFloat(subscriptionPricePaid || '0');
  const walletBalance = parseFloat(balance || '0');
  const lowBalanceWarning = isPaid && autoRenew && walletBalance < renewalPrice && renewalPrice > 0;
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: colors.backgroundColor }}>
        <CLoader />
      </div>
    );
  }

  return (
    <div
      className="p-4 md:p-6 lg:p-8 min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO title="Wallet" noIndex />
      <div className="lg:max-w-5xl lg:mx-auto">
        <CText type="B24" color={colors.textColor} style={{ marginBottom: '24px' }}>
          {strings.wallet}
        </CText>

        {/* Section 1: Current Plan Card */}
        <div
          className="rounded-2xl p-5 md:p-6 mb-5 border"
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: colors.borderColor,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CText type="B18" color={colors.textColor}>
                Current Plan
              </CText>
              <span
                className="px-3 py-1 rounded-full text-sm font-semibold"
                style={{
                  backgroundColor: tierConfig.bg,
                  color: tierConfig.color,
                }}
              >
                {isPaid ? `${tierConfig.label} - ${tenureLabel}` : tierConfig.label}
              </span>
            </div>
            <button
              onClick={() => navigate(routes.pricing)}
              className="text-sm font-medium hover:opacity-80 transition-opacity"
              style={{ color: colors.primary, cursor: 'pointer', background: 'none', border: 'none' }}
            >
              View all plans
            </button>
          </div>

          {isPaid && subscriptionExpires ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <CText type="r14" color={colors.grayScale500}>
                  {`Expires on ${formatExpiryDate(subscriptionExpires)}`}
                </CText>
                <CButton
                  title="Change Plan"
                  onPress={() => navigate(routes.pricing)}
                  size="sm"
                />
              </div>

              {/* Auto-renew toggle */}
              <div
                className="flex items-center justify-between p-3 rounded-xl"
                style={{
                  backgroundColor: colors.backgroundColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <div>
                  <CText type="M14" color={colors.textColor}>
                    Auto-renew
                  </CText>
                  {renewalPrice > 0 && (
                    <CText type="r12" color={colors.grayScale500}>
                      {`Renews at \u20B9${renewalPrice.toLocaleString('en-IN')} from wallet`}
                    </CText>
                  )}
                </div>
                <button
                  onClick={handleToggleAutoRenew}
                  disabled={autoRenewLoading}
                  className="relative w-11 h-6 rounded-full transition-colors duration-200"
                  style={{
                    backgroundColor: autoRenew ? colors.primary : (colors.grayScale400 || '#D4D4D4'),
                    border: 'none',
                    cursor: autoRenewLoading ? 'not-allowed' : 'pointer',
                    opacity: autoRenewLoading ? 0.6 : 1,
                  }}
                >
                  <div
                    className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
                    style={{
                      transform: autoRenew ? 'translateX(22px)' : 'translateX(2px)',
                    }}
                  />
                </button>
              </div>

              {/* Low balance warning */}
              {lowBalanceWarning && (
                <div
                  className="mt-3 p-3 rounded-xl flex items-start gap-2"
                  style={{
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #FDE68A',
                  }}
                >
                  <span style={{ fontSize: 16 }}>&#x26A0;</span>
                  <div>
                    <CText type="M12" color="#92400E">
                      Low wallet balance
                    </CText>
                    <CText type="r12" color="#A16207">
                      {`Your balance (\u20B9${walletBalance.toLocaleString('en-IN')}) is less than the renewal price (\u20B9${renewalPrice.toLocaleString('en-IN')}). Add money to avoid losing your subscription.`}
                    </CText>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: colors.primary50 || '#F8F5FF', color: colors.primary }}
                >
                  Unlimited queries
                </span>
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: colors.primary50 || '#F8F5FF', color: colors.primary }}
                >
                  All 15+ buckets
                </span>
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: colors.primary50 || '#F8F5FF', color: colors.primary }}
                >
                  Expert discounts
                </span>
              </div>
              <CButton
                title="Upgrade to Pro"
                onPress={() => navigate(routes.pricing)}
                size="sm"
              />
            </div>
          )}
        </div>

        {/* Balance Card */}
        <div
          className="rounded-2xl p-5 md:p-6 mb-5"
          style={{
            background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 50%, ${colors.gradient1} 100%)`,
            color: colors.white,
          }}
        >
          <CText type="r12" color={colors.white} style={{ marginBottom: '8px' }}>
            {strings.availableBalance}
          </CText>
          <CText type="B36" color={colors.white} style={{ marginBottom: '8px' }}>
            {`\u20B9 ${parseFloat(balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </CText>
        </div>

        {/* Section 2: Usage Summary */}
        <div
          className="rounded-2xl p-5 md:p-6 mb-5 border"
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: colors.borderColor,
          }}
        >
          <CText type="B18" color={colors.textColor} style={{ marginBottom: '16px' }}>
            Usage Summary
          </CText>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Copilot Queries */}
            <div
              className="p-4 rounded-xl border"
              style={{ backgroundColor: colors.backgroundColor, borderColor: colors.borderColor }}
            >
              <CText type="r12" color={colors.grayScale500} style={{ marginBottom: '8px' }}>
                Copilot Queries
              </CText>
              {isPaid ? (
                <CText type="B18" color={colors.green || '#1DCE5C'}>
                  Unlimited
                </CText>
              ) : (
                <>
                  <CText type="B18" color={colors.textColor}>
                    {`${copilotQueriesUsed} of ${queriesLimit} used`}
                  </CText>
                  {/* Progress bar */}
                  <div
                    className="w-full h-2 rounded-full mt-2"
                    style={{ backgroundColor: colors.grayScale200 || '#E5E5E5' }}
                  >
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(100, (copilotQueriesUsed / queriesLimit) * 100)}%`,
                        backgroundColor: copilotQueriesUsed >= queriesLimit
                          ? (colors.alertColor || '#E82C81')
                          : colors.primary,
                      }}
                    />
                  </div>
                  {queriesResetAt && (
                    <CText type="r12" color={colors.grayScale500} style={{ marginTop: '4px' }}>
                      {`Resets ${formatExpiryDate(queriesResetAt)}`}
                    </CText>
                  )}
                </>
              )}
            </div>

            {/* Buckets Access */}
            <div
              className="p-4 rounded-xl border"
              style={{ backgroundColor: colors.backgroundColor, borderColor: colors.borderColor }}
            >
              <CText type="r12" color={colors.grayScale500} style={{ marginBottom: '8px' }}>
                Buckets Access
              </CText>
              <CText type="B18" color={colors.textColor}>
                {benefits.buckets}
              </CText>
            </div>

            {/* Expert Discount */}
            <div
              className="p-4 rounded-xl border"
              style={{ backgroundColor: colors.backgroundColor, borderColor: colors.borderColor }}
            >
              <CText type="r12" color={colors.grayScale500} style={{ marginBottom: '8px' }}>
                Expert Discount
              </CText>
              <CText type="B18" color={isPaid ? (colors.green || '#1DCE5C') : colors.textColor}>
                {benefits.discount}
              </CText>
            </div>
          </div>
        </div>

        {/* Add Money Section */}
        <div
          className="rounded-2xl p-5 md:p-6 mb-5 border"
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: colors.borderColor,
          }}
        >
          <CText type="B18" color={colors.textColor} style={{ marginBottom: '16px' }}>
            Add Money
          </CText>

          {/* Preset Amount Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESET_AMOUNTS.map((preset) => (
              <button
                key={preset}
                onClick={() => { setDepositAmount(String(preset)); setError(''); }}
                className="px-4 py-2 rounded-lg border transition-colors duration-200"
                style={{
                  backgroundColor: depositAmount === String(preset) ? colors.primary : colors.backgroundColor,
                  borderColor: depositAmount === String(preset) ? colors.primary : colors.borderColor,
                  color: depositAmount === String(preset) ? colors.white : colors.textColor,
                  cursor: 'pointer',
                }}
              >
                {`\u20B9${preset}`}
              </button>
            ))}
          </div>

          {/* Amount Input */}
          <div className="flex gap-3 items-start">
            <div className="flex-1">
              <input
                type="number"
                value={depositAmount}
                onChange={(e) => { setDepositAmount(e.target.value); setError(''); }}
                placeholder="Enter amount"
                min="1"
                className="w-full px-4 py-3 rounded-xl border outline-none"
                style={{
                  backgroundColor: colors.backgroundColor,
                  borderColor: error ? (colors.alertColor || '#E82C81') : colors.borderColor,
                  color: colors.textColor,
                }}
              />
              {error && (
                <CText type="r12" color={colors.alertColor || '#E82C81'} style={{ marginTop: '4px' }}>
                  {error}
                </CText>
              )}
            </div>
            <CButton
              title={depositing ? 'Processing...' : 'Add Money'}
              onPress={handleDeposit}
              disabled={depositing || !depositAmount}
              frontIcon={!depositing ? React.createElement(FiPlus as any, { size: 18, color: colors.white }) : undefined}
              size="md"
            />
          </div>
        </div>

        {/* Transaction History with Filter Tabs */}
        <div>
          <CText type="B18" color={colors.textColor} style={{ marginBottom: '16px' }}>
            Transaction History
          </CText>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {(['ALL', 'DEPOSIT', 'SUBSCRIPTION', 'BOOKING'] as TransactionFilter[]).map((filter) => (
              <button
                key={filter}
                onClick={() => setTxFilter(filter)}
                className="px-4 py-2 rounded-lg border text-sm whitespace-nowrap transition-colors duration-200"
                style={{
                  backgroundColor: txFilter === filter ? colors.primary : colors.backgroundColor,
                  borderColor: txFilter === filter ? colors.primary : colors.borderColor,
                  color: txFilter === filter ? colors.white : colors.textColor,
                  cursor: 'pointer',
                }}
              >
                {filter === 'ALL' ? 'All' : filter.charAt(0) + filter.slice(1).toLowerCase() + 's'}
              </button>
            ))}
          </div>

          {filteredTransactions.length === 0 ? (
            <div
              className="flex items-center justify-center p-8 rounded-xl border"
              style={{
                backgroundColor: colors.inputBackground,
                borderColor: colors.borderColor,
              }}
            >
              <CText type="r14" color={colors.grayScale500}>
                {txFilter === 'ALL' ? 'No transactions yet' : `No ${txFilter.toLowerCase()} transactions`}
              </CText>
            </div>
          ) : (
            filteredTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex justify-between items-center p-4 rounded-xl mb-2 border"
                style={{
                  backgroundColor: colors.inputBackground,
                  borderColor: colors.borderColor,
                }}
              >
                <div className="flex items-center gap-3">
                  {/* Transaction type icon */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                    style={{
                      backgroundColor: transaction.transaction_type === 'DEPOSIT'
                        ? (colors.green1 || '#E9FCEF')
                        : (colors.primary50 || '#F8F5FF'),
                    }}
                  >
                    {transaction.transaction_type === 'DEPOSIT' ? '\u2191' : '\u2193'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CText type="M14" color={colors.textColor}>
                        {transaction.transaction_type === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'}
                      </CText>
                      {transaction.status !== 'SUCCESS' && (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: transaction.status === 'PENDING' ? '#FEF3C7' : '#FEE2E2',
                            color: getStatusColor(transaction.status),
                          }}
                        >
                          {transaction.status}
                        </span>
                      )}
                    </div>
                    <CText type="r12" color={colors.grayScale500}>
                      {formatDate(transaction.created_at)}
                    </CText>
                    {transaction.description && (
                      <CText type="r12" color={colors.grayScale500}>
                        {transaction.description}
                      </CText>
                    )}
                  </div>
                </div>
                <CText
                  type="M16"
                  color={
                    transaction.status === 'FAILED'
                      ? (colors.grayScale400 || '#A3A3A3')
                      : transaction.transaction_type === 'DEPOSIT'
                        ? (colors.green || '#1DCE5C')
                        : colors.textColor
                  }
                  style={transaction.status === 'FAILED' ? { textDecoration: 'line-through' } : {}}
                >
                  {transaction.transaction_type === 'DEPOSIT' ? '+' : '-'}
                  {`\u20B9${parseFloat(transaction.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                </CText>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Wallet;
